/**
 * Work permit & immigration-reporting cluster (L3) — /work-permit/*
 *
 * Powers /work-permit, /work-permit/topic/$topic, /work-permit/questions
 * and /work-permit/q/$slug.
 *
 * FACTUAL GUARDRAILS
 * - No service prices anywhere (SHOW_PUBLIC_PRICES=false). Every CTA points
 *   to phone / LINE / email.
 * - Statutory references are limited to provisions we can name:
 *   พระราชกำหนดการบริหารจัดการการทำงานของคนต่างด้าว พ.ศ. 2560 (และที่แก้ไข
 *   เพิ่มเติม พ.ศ. 2561) สำหรับใบอนุญาตทำงาน และ พ.ร.บ. คนเข้าเมือง พ.ศ. 2522
 *   มาตรา 37(5) (รายงานตัว 90 วัน), มาตรา 38 (แจ้งที่พัก TM.30),
 *   มาตรา 39 (Re-entry Permit).
 * - Operational practice that varies by office (เอกสารเพิ่มเติม คิว นัดหมาย
 *   แบบฟอร์มท้องถิ่น) is written as "ยืนยันกับหน่วยงานผู้รับ" — never asserted.
 * - No guaranteed outcome, no guaranteed turnaround.
 */

export const WP_REVIEWED_TH = "สิงหาคม 2569";
export const WP_REVIEWED_ISO = "2026-08-04T00:00:00.000Z";

export type WpTopic = {
  slug: string;
  th: string;
  en: string;
  /** หน่วยงานผู้รับหลัก */
  authorityTh: string;
  /** ช่องทางยื่นตามที่หน่วยงานเปิดให้ */
  channelsTh: string[];
  /** 50–80 คำ answer-first */
  summaryTh: string;
  /** ตาราง atomic facts (ไม่มีราคา) */
  factsTh: { label: string; value: string }[];
  requirementsTh: string[];
  stepsTh: { title: string; detail: string }[];
  tipsTh: string[];
  warningsTh: string[];
  citationTh: string;
};


export const WP_TOPICS: WpTopic[] = [
  {
    slug: "work-permit-new",
    th: "ขอใบอนุญาตทำงานครั้งแรก",
    en: "New work permit application",
    authorityTh: "กรมการจัดหางาน กระทรวงแรงงาน",
    channelsTh: ["สำนักงานจัดหางานกรุงเทพมหานครพื้นที่ / จังหวัด", "ระบบ e-Workpermit ตามที่กรมการจัดหางานเปิดให้บริการ", "ศูนย์บริการวีซ่าและใบอนุญาตทำงาน (One Stop Service) สำหรับกิจการที่เข้าเงื่อนไข"],
    summaryTh:
      "การขอใบอนุญาตทำงานครั้งแรกต้องมีวีซ่าประเภทที่ทำงานได้อยู่ก่อน แล้วยื่นคำขอที่กรมการจัดหางานพร้อมเอกสารนายจ้างและเอกสารลูกจ้าง เจ้าหน้าที่ตรวจคุณสมบัติตำแหน่งงาน สัดส่วนพนักงานไทย และทุนจดทะเบียนตามหลักเกณฑ์ ระยะเวลาพิจารณาเป็นวันทำการและขึ้นกับความครบถ้วนของเอกสารและคิวของสำนักงานที่ยื่น",
    factsTh: [
      { label: "หน่วยงานผู้อนุญาต", value: "กรมการจัดหางาน กระทรวงแรงงาน" },
      { label: "กฎหมายหลัก", value: "พ.ร.ก. การบริหารจัดการการทำงานของคนต่างด้าว พ.ศ. 2560 และที่แก้ไขเพิ่มเติม" },
      { label: "เงื่อนไขก่อนยื่น", value: "ต้องถือวีซ่าประเภทที่ประกอบอาชีพได้ (เช่น Non-B) และมีนายจ้างที่ตรวจสอบได้" },
      { label: "อายุใบอนุญาต", value: "ตามที่นายทะเบียนกำหนด โดยทั่วไปไม่เกินอายุการอนุญาตให้อยู่ในราชอาณาจักร" },
      { label: "ค่าธรรมเนียม", value: "เป็นอัตราราชการตามกฎกระทรวง — ตรวจสอบอัตราปัจจุบันกับกรมการจัดหางาน" },
    ],
    requirementsTh: [
      "หนังสือเดินทางพร้อมวีซ่าประเภทที่ทำงานได้ และตราประทับขาเข้าล่าสุด",
      "แบบคำขอตามที่กรมการจัดหางานกำหนด พร้อมรูปถ่ายตามขนาดที่ระบุ",
      "ใบรับรองแพทย์ตามหลักเกณฑ์ที่หน่วยงานกำหนด",
      "หนังสือรับรองนิติบุคคลและบัญชีรายชื่อผู้ถือหุ้นฉบับล่าสุด",
      "หลักฐานการยื่นภาษีและประกันสังคมของนายจ้างตามที่สำนักงานร้องขอ",
      "สัญญาจ้าง ระบุตำแหน่ง หน้าที่ และสถานที่ทำงาน",
      "วุฒิการศึกษา/ใบรับรองประสบการณ์ พร้อมคำแปลและการรับรองตามที่หน่วยงานกำหนด",
    ],
    stepsTh: [
      { title: "ตรวจคุณสมบัติและตำแหน่งงาน", detail: "ตรวจว่าตำแหน่งไม่อยู่ในงานที่ห้ามคนต่างด้าวทำ และนายจ้างมีคุณสมบัติตามหลักเกณฑ์ทุนจดทะเบียนและสัดส่วนพนักงานไทย" },
      { title: "จัดเตรียมเอกสารสองฝั่ง", detail: "รวบรวมเอกสารนายจ้าง (นิติบุคคล ภาษี ประกันสังคม) และเอกสารลูกจ้าง (พาสปอร์ต วุฒิ ใบรับรองแพทย์) พร้อมคำแปลที่ผ่านการรับรอง" },
      { title: "ยื่นคำขอต่อกรมการจัดหางาน", detail: "ยื่นที่สำนักงานจัดหางานพื้นที่ที่สถานประกอบการตั้งอยู่ หรือช่องทางออนไลน์/One Stop Service ตามที่เปิดให้บริการ" },
      { title: "ชี้แจงเอกสารเพิ่มเติมและรับใบอนุญาต", detail: "หากเจ้าหน้าที่ขอเอกสารเพิ่ม ให้ยื่นภายในกำหนด แล้วรับใบอนุญาตทำงาน ตรวจความถูกต้องของชื่อ ตำแหน่ง และนายจ้างทันทีที่ได้รับ" },
      { title: "ต่อยอดกับตรวจคนเข้าเมือง", detail: "นำใบอนุญาตทำงานไปใช้ประกอบการขออยู่ต่อ 1 ปี รายงานตัว 90 วัน และขอ Re-entry Permit ตามความจำเป็น" },
    ],
    tipsTh: [
      "ตรวจว่าตำแหน่งงานไม่อยู่ในบัญชีงานที่ห้ามคนต่างด้าวทำก่อนเซ็นสัญญาจ้าง",
      "ให้ชื่อตำแหน่งในสัญญาจ้าง ใบอนุญาตทำงาน และเอกสารวีซ่าตรงกันทุกฉบับ",
    ],
    warningsTh: [
      "เริ่มทำงานก่อนได้รับใบอนุญาตมีความเสี่ยงตามกฎหมายทั้งฝ่ายนายจ้างและลูกจ้าง",
      "วุฒิการศึกษาจากต่างประเทศที่ยังไม่ได้แปลและรับรองตามเส้นทางที่กำหนด มักถูกขอเอกสารเพิ่ม",
    ],
    citationTh:
      "อ้างอิง: พ.ร.ก. การบริหารจัดการการทำงานของคนต่างด้าว พ.ศ. 2560 และที่แก้ไขเพิ่มเติม (doe.go.th) — ตรวจสอบหลักเกณฑ์ปัจจุบันกับกรมการจัดหางานก่อนยื่น",
  },
  {
    slug: "work-permit-renewal",
    th: "ต่ออายุใบอนุญาตทำงาน",
    en: "Work permit renewal",
    authorityTh: "กรมการจัดหางาน กระทรวงแรงงาน",
    channelsTh: ["สำนักงานจัดหางานพื้นที่/จังหวัด", "ระบบออนไลน์ตามที่กรมการจัดหางานเปิดให้บริการ", "One Stop Service สำหรับกิจการที่เข้าเงื่อนไข"],
    summaryTh:
      "การต่ออายุใบอนุญาตทำงานต้องยื่นก่อนวันสิ้นอายุ โดยแนบหลักฐานว่ายังทำงานกับนายจ้างเดิมในตำแหน่งเดิม พร้อมเอกสารภาษีและประกันสังคมล่าสุดของทั้งบริษัทและลูกจ้าง กระบวนการมักเดินคู่กับการขออยู่ต่อในราชอาณาจักรที่ตรวจคนเข้าเมือง จึงควรวางลำดับวันยื่นทั้งสองหน่วยงานให้สอดคล้องกัน",
    factsTh: [
      { label: "หน่วยงานผู้อนุญาต", value: "กรมการจัดหางาน" },
      { label: "กำหนดยื่น", value: "ก่อนใบอนุญาตสิ้นอายุ — ยื่นล่วงหน้าเพื่อเผื่อเวลาขอเอกสารเพิ่ม" },
      { label: "เอกสารเด่นที่มักถูกขอ", value: "ภ.ง.ด.1/ภ.ง.ด.91 ประกันสังคม และหนังสือรับรองนิติบุคคลฉบับล่าสุด" },
      { label: "เชื่อมโยงกับ ตม.", value: "ใช้ประกอบการขออยู่ต่อ 1 ปี ที่สำนักงานตรวจคนเข้าเมือง" },
      { label: "ค่าธรรมเนียม", value: "อัตราราชการตามกฎกระทรวง — ตรวจสอบอัตราปัจจุบันกับกรมการจัดหางาน" },
    ],
    requirementsTh: [
      "ใบอนุญาตทำงานฉบับปัจจุบัน",
      "หนังสือเดินทางพร้อมหน้าวีซ่าและตราประทับอยู่ต่อล่าสุด",
      "หนังสือรับรองนิติบุคคลและบัญชีรายชื่อผู้ถือหุ้นฉบับล่าสุด",
      "หลักฐานการเสียภาษีเงินได้และการนำส่งประกันสังคม",
      "หนังสือรับรองการจ้างงานจากนายจ้าง",
    ],
    stepsTh: [
      { title: "ตรวจวันสิ้นอายุและวางแผนล่วงหน้า", detail: "นับถอยหลังจากวันสิ้นอายุ แล้วกำหนดวันยื่นให้เหลือเวลาสำหรับกรณีเจ้าหน้าที่ขอเอกสารเพิ่ม" },
      { title: "อัปเดตเอกสารนายจ้าง", detail: "ขอหนังสือรับรองนิติบุคคลฉบับใหม่ และรวบรวมหลักฐานภาษี/ประกันสังคมงวดล่าสุด" },
      { title: "ยื่นคำขอต่ออายุ", detail: "ยื่นต่อสำนักงานจัดหางานพื้นที่ที่สถานประกอบการตั้งอยู่ หรือช่องทางที่เปิดให้บริการ" },
      { title: "ประสานกับการขออยู่ต่อที่ ตม.", detail: "เมื่อได้รับผลการต่ออายุ ให้ใช้ประกอบการขออยู่ต่อในราชอาณาจักร และปรับปรุงกำหนดรายงานตัว 90 วันให้ตรงกัน" },
    ],
    tipsTh: [
      "อย่ารอจนสัปดาห์สุดท้าย — คิวและการขอเอกสารเพิ่มเป็นเหตุให้ยื่นไม่ทันบ่อยที่สุด",
      "จัดแฟ้มภาษี/ประกันสังคมรายเดือนไว้ล่วงหน้า จะลดเวลาทำเรื่องได้มาก",
    ],
    warningsTh: [
      "ใบอนุญาตทำงานสิ้นอายุแล้วจะไม่ใช่การ 'ต่ออายุ' อีกต่อไป และอาจต้องเริ่มกระบวนการใหม่",
      "การอยู่ต่อในราชอาณาจักรกับใบอนุญาตทำงานเป็นคนละเรื่องกัน ต้องดูแลทั้งสองส่วนพร้อมกัน",
    ],
    citationTh:
      "อ้างอิง: กรมการจัดหางาน (doe.go.th) และ พ.ร.ก. การบริหารจัดการการทำงานของคนต่างด้าว พ.ศ. 2560 — ยืนยันรายการเอกสารกับสำนักงานที่ยื่น",
  },
  {
    slug: "work-permit-change",
    th: "แก้ไขรายการในใบอนุญาตทำงาน (นายจ้าง / ตำแหน่ง / สถานที่)",
    en: "Work permit amendment",
    authorityTh: "กรมการจัดหางาน กระทรวงแรงงาน",
    channelsTh: ["สำนักงานจัดหางานพื้นที่/จังหวัด", "ช่องทางออนไลน์ตามที่เปิดให้บริการ"],
    summaryTh:
      "เมื่อเปลี่ยนนายจ้าง ตำแหน่งงาน ประเภทงาน หรือสถานที่ทำงาน ต้องดำเนินการแก้ไขรายการในใบอนุญาตทำงานตามขั้นตอนของกรมการจัดหางาน ไม่ใช่แก้ไขเอกสารเอง การเปลี่ยนนายจ้างมักต้องมีหลักฐานการสิ้นสุดการจ้างเดิมและเอกสารของนายจ้างใหม่ครบชุด ควรยืนยันลำดับขั้นตอนกับสำนักงานที่ยื่นก่อนลาออก",
    factsTh: [
      { label: "หน่วยงานผู้อนุญาต", value: "กรมการจัดหางาน" },
      { label: "กรณีที่ต้องแจ้ง", value: "เปลี่ยนนายจ้าง ตำแหน่ง ลักษณะงาน หรือสถานที่ทำงาน" },
      { label: "เอกสารเด่น", value: "หลักฐานสิ้นสุดการจ้างเดิม + ชุดเอกสารนายจ้างใหม่" },
      { label: "ผลต่อวีซ่า", value: "การเปลี่ยนนายจ้างมักกระทบเงื่อนไขการอยู่ต่อ ต้องประสาน ตม. คู่กัน" },
    ],
    requirementsTh: [
      "ใบอนุญาตทำงานฉบับปัจจุบัน",
      "หนังสือแจ้งการสิ้นสุดการจ้างจากนายจ้างเดิม (ถ้าเปลี่ยนนายจ้าง)",
      "ชุดเอกสารนิติบุคคลของนายจ้างใหม่",
      "สัญญาจ้างใหม่ระบุตำแหน่งและสถานที่ทำงาน",
      "หนังสือเดินทางพร้อมหน้าวีซ่าปัจจุบัน",
    ],
    stepsTh: [
      { title: "ยืนยันลำดับขั้นตอนกับสำนักงาน", detail: "สอบถามสำนักงานจัดหางานที่ยื่นว่าต้องแจ้งสิ้นสุดการจ้างก่อนหรือยื่นพร้อมกัน เพื่อไม่ให้เกิดช่วงว่างที่ทำงานไม่ได้" },
      { title: "รวบรวมเอกสารนายจ้างใหม่", detail: "ขอหนังสือรับรองนิติบุคคลและเอกสารประกอบฉบับล่าสุดของนายจ้างใหม่" },
      { title: "ยื่นคำขอแก้ไขรายการ", detail: "ยื่นแบบคำขอพร้อมเอกสาร และรับใบนัด/ใบรับคำขอไว้เป็นหลักฐาน" },
      { title: "ปรับสถานะกับตรวจคนเข้าเมือง", detail: "เมื่อรายการในใบอนุญาตเปลี่ยน ให้ดำเนินการด้านการอยู่ต่อและแจ้งที่พักให้สอดคล้องกัน" },
    ],
    tipsTh: [
      "อย่าลาออกก่อนยืนยันลำดับขั้นตอนกับสำนักงานที่ยื่น เพราะช่วงรอยต่ออาจทำให้สิทธิอยู่ต่อสะดุด",
      "เก็บสำเนาสัญญาจ้างเดิมและหนังสือสิ้นสุดการจ้างไว้ทุกฉบับ",
    ],
    warningsTh: [
      "ทำงานกับนายจ้างใหม่ก่อนแก้ไขรายการเสร็จมีความเสี่ยงตามกฎหมาย",
      "ตำแหน่งใหม่อาจอยู่ในงานที่ห้ามคนต่างด้าวทำ — ตรวจก่อนตกลงรับงาน",
    ],
    citationTh: "อ้างอิง: กรมการจัดหางาน (doe.go.th) — แนวปฏิบัติต่างกันตามสำนักงานพื้นที่ ควรยืนยันก่อนยื่น",
  },
  {
    slug: "tm47-90-day",
    th: "รายงานตัว 90 วัน (ตม.47)",
    en: "90-day address report (TM.47)",
    authorityTh: "สำนักงานตรวจคนเข้าเมือง",
    channelsTh: ["ยื่นด้วยตนเองที่สำนักงานตรวจคนเข้าเมืองพื้นที่", "ยื่นทางไปรษณีย์ลงทะเบียน", "ระบบออนไลน์ตามที่สำนักงานตรวจคนเข้าเมืองเปิดให้บริการ", "มอบอำนาจให้ผู้อื่นยื่นแทนตามที่สำนักงานกำหนด"],
    summaryTh:
      "คนต่างด้าวที่ได้รับอนุญาตให้อยู่ในราชอาณาจักรเกิน 90 วัน ต้องแจ้งที่พักอาศัยต่อพนักงานเจ้าหน้าที่ทุก 90 วัน ตามพระราชบัญญัติคนเข้าเมือง พ.ศ. 2522 มาตรา 37(5) ช่องทางการยื่นและกรอบเวลาที่รับล่วงหน้า/ย้อนหลังเป็นไปตามที่สำนักงานตรวจคนเข้าเมืองประกาศ ควรตรวจกำหนดวันจากใบรับครั้งล่าสุดทุกครั้ง",
    factsTh: [
      { label: "หน่วยงานผู้รับ", value: "สำนักงานตรวจคนเข้าเมือง (immigration.go.th)" },
      { label: "ฐานกฎหมาย", value: "พ.ร.บ. คนเข้าเมือง พ.ศ. 2522 มาตรา 37(5)" },
      { label: "รอบการแจ้ง", value: "ทุก 90 วันที่อยู่ต่อเนื่องในราชอาณาจักร" },
      { label: "หลักฐานที่ได้", value: "ใบรับแจ้ง (ส่วนท้าย ตม.47) ที่ระบุกำหนดครั้งถัดไป" },
      { label: "การนับรอบใหม่", value: "การเดินทางออกนอกประเทศทำให้เริ่มนับรอบใหม่เมื่อกลับเข้ามา" },
    ],
    requirementsTh: [
      "หนังสือเดินทางเล่มจริงพร้อมสำเนาหน้าข้อมูลส่วนตัว หน้าวีซ่า และตราประทับขาเข้าล่าสุด",
      "บัตรขาเข้า-ออก (ถ้ามี)",
      "ใบรับแจ้ง 90 วันครั้งก่อน",
      "แบบ ตม.47 ที่กรอกครบถ้วน",
      "หนังสือมอบอำนาจและสำเนาบัตรผู้รับมอบอำนาจ กรณีให้ผู้อื่นยื่นแทน",
    ],
    stepsTh: [
      { title: "ตรวจกำหนดวันจากใบรับครั้งล่าสุด", detail: "ดูวันครบกำหนดที่ระบุไว้ แล้วตั้งเตือนล่วงหน้าอย่างน้อย 2 สัปดาห์" },
      { title: "เลือกช่องทางยื่น", detail: "เลือกยื่นด้วยตนเอง ทางไปรษณีย์ ออนไลน์ หรือมอบอำนาจ ตามช่องทางที่สำนักงานพื้นที่นั้นเปิดให้บริการจริง" },
      { title: "ยื่นภายในกรอบเวลาที่ประกาศ", detail: "ยื่นตามกรอบเวลาที่สำนักงานตรวจคนเข้าเมืองกำหนด และเก็บหลักฐานการส่ง/ใบรับไว้ทุกครั้ง" },
      { title: "จัดเก็บใบรับและบันทึกกำหนดถัดไป", detail: "แนบใบรับไว้กับหนังสือเดินทาง และบันทึกวันครบกำหนดครั้งถัดไปทันที" },
    ],
    tipsTh: [
      "ถ่ายรูปใบรับแจ้งเก็บไว้ในโทรศัพท์ เผื่อเอกสารต้นฉบับสูญหาย",
      "ถ้าย้ายที่พัก ให้ปรับปรุงข้อมูลที่พักให้ตรงก่อนถึงรอบรายงานตัวถัดไป",
    ],
    warningsTh: [
      "การแจ้งล่าช้ากว่ากำหนดอาจถูกเปรียบเทียบปรับตามกฎหมายคนเข้าเมือง",
      "การรายงานตัว 90 วันไม่ใช่การต่อวีซ่า และไม่ทำให้สิทธิอยู่ต่อยาวขึ้น",
    ],
    citationTh: "อ้างอิง: พ.ร.บ. คนเข้าเมือง พ.ศ. 2522 มาตรา 37(5) · สำนักงานตรวจคนเข้าเมือง (immigration.go.th)",
  },
  {
    slug: "tm30-notification",
    th: "แจ้งที่พักคนต่างด้าว (ตม.30)",
    en: "TM.30 accommodation notification",
    authorityTh: "สำนักงานตรวจคนเข้าเมือง",
    channelsTh: ["ยื่นด้วยตนเองที่สำนักงานตรวจคนเข้าเมืองพื้นที่", "ระบบแจ้งที่พักออนไลน์ตามที่สำนักงานเปิดให้บริการ"],
    summaryTh:
      "เจ้าบ้าน เจ้าของ หรือผู้ครอบครองเคหสถาน รวมถึงผู้จัดการโรงแรม มีหน้าที่แจ้งการเข้าพักของคนต่างด้าวต่อพนักงานเจ้าหน้าที่ตามพระราชบัญญัติคนเข้าเมือง พ.ศ. 2522 มาตรา 38 การแจ้งที่พักให้เป็นปัจจุบันช่วยให้การขออยู่ต่อ การรายงานตัว 90 วัน และการขอหนังสือรับรองถิ่นที่อยู่ราบรื่นขึ้น",
    factsTh: [
      { label: "ผู้มีหน้าที่แจ้ง", value: "เจ้าบ้าน/เจ้าของ/ผู้ครอบครองเคหสถาน หรือผู้จัดการโรงแรม" },
      { label: "ฐานกฎหมาย", value: "พ.ร.บ. คนเข้าเมือง พ.ศ. 2522 มาตรา 38" },
      { label: "เมื่อใดต้องแจ้ง", value: "เมื่อมีคนต่างด้าวเข้าพัก ตามกรอบเวลาที่กฎหมายกำหนด" },
      { label: "ผลต่อเรื่องอื่น", value: "ตม. มักขอหลักฐานการแจ้งที่พักประกอบการขออยู่ต่อและรายงานตัว 90 วัน" },
    ],
    requirementsTh: [
      "สำเนาหนังสือเดินทางของผู้เข้าพัก (หน้าข้อมูล วีซ่า ตราขาเข้า)",
      "สำเนาทะเบียนบ้านและบัตรประชาชนของเจ้าบ้าน หรือหนังสือรับรองนิติบุคคลกรณีเจ้าของเป็นนิติบุคคล",
      "สัญญาเช่าหรือหลักฐานการครอบครองเคหสถาน",
      "แบบ ตม.30 ที่กรอกครบถ้วน",
    ],
    stepsTh: [
      { title: "ระบุผู้มีหน้าที่แจ้ง", detail: "ตรวจว่าเจ้าบ้าน เจ้าของห้อง หรือผู้จัดการอาคารเป็นผู้แจ้ง ไม่ใช่ตัวผู้เช่าเสมอไป" },
      { title: "รวบรวมเอกสารที่พัก", detail: "เตรียมทะเบียนบ้าน บัตรเจ้าบ้าน สัญญาเช่า และสำเนาหนังสือเดินทางผู้เข้าพัก" },
      { title: "แจ้งภายในกรอบเวลาที่กำหนด", detail: "ยื่นผ่านช่องทางที่สำนักงานพื้นที่เปิดให้บริการ และเก็บหลักฐานการรับแจ้ง" },
      { title: "แจ้งใหม่เมื่อมีการเปลี่ยนแปลง", detail: "เมื่อย้ายที่พัก เปลี่ยนผู้เช่า หรือกลับเข้าประเทศใหม่ ให้ตรวจว่าต้องแจ้งซ้ำหรือไม่" },
    ],
    tipsTh: [
      "ผู้เช่าควรขอสำเนาหลักฐานการแจ้งที่พักจากเจ้าของห้องเก็บไว้เอง",
      "คอนโดที่มีนิติบุคคลอาคารชุดมักมีขั้นตอนภายใน ควรสอบถามฝ่ายจัดการก่อน",
    ],
    warningsTh: [
      "ไม่แจ้งที่พักตามกำหนดอาจมีโทษปรับตามกฎหมายคนเข้าเมือง",
      "ที่อยู่ในระบบไม่ตรงกับที่พักจริงเป็นเหตุให้เรื่องขออยู่ต่อถูกขอเอกสารเพิ่ม",
    ],
    citationTh: "อ้างอิง: พ.ร.บ. คนเข้าเมือง พ.ศ. 2522 มาตรา 38 · สำนักงานตรวจคนเข้าเมือง (immigration.go.th)",
  },
  {
    slug: "re-entry-permit",
    th: "Re-entry Permit (รักษาสิทธิการอยู่ต่อเมื่อเดินทางออกนอกประเทศ)",
    en: "Re-entry permit",
    authorityTh: "สำนักงานตรวจคนเข้าเมือง",
    channelsTh: ["สำนักงานตรวจคนเข้าเมืองพื้นที่", "เคาน์เตอร์ที่ท่าอากาศยานตามที่เปิดให้บริการ", "ระบบออนไลน์ตามที่สำนักงานเปิดให้บริการ"],
    summaryTh:
      "ผู้ที่ได้รับอนุญาตให้อยู่ในราชอาณาจักรและต้องเดินทางออกนอกประเทศชั่วคราว ควรขอ Re-entry Permit ก่อนออกเดินทาง มิฉะนั้นการอนุญาตให้อยู่ต่อที่เหลืออยู่จะสิ้นผลเมื่อเดินทางออก มีทั้งแบบครั้งเดียวและหลายครั้ง เลือกตามแผนการเดินทางจริงภายในช่วงเวลาที่ได้รับอนุญาตให้อยู่",
    factsTh: [
      { label: "หน่วยงานผู้รับ", value: "สำนักงานตรวจคนเข้าเมือง" },
      { label: "ฐานกฎหมาย", value: "พ.ร.บ. คนเข้าเมือง พ.ศ. 2522 มาตรา 39" },
      { label: "ประเภท", value: "Single (ครั้งเดียว) และ Multiple (หลายครั้ง)" },
      { label: "อายุการใช้", value: "ไม่เกินวันสิ้นสุดการอนุญาตให้อยู่ในราชอาณาจักร" },
      { label: "ค่าธรรมเนียม", value: "อัตราราชการตามที่กำหนด — ตรวจสอบกับสำนักงานตรวจคนเข้าเมือง" },
    ],
    requirementsTh: [
      "หนังสือเดินทางเล่มจริง",
      "แบบคำขอ ตม.8 พร้อมรูปถ่ายตามขนาดที่กำหนด",
      "สำเนาหน้าหนังสือเดินทาง หน้าวีซ่า และตราประทับอยู่ต่อล่าสุด",
      "บัตรขาเข้า-ออก (ถ้ามี)",
    ],
    stepsTh: [
      { title: "ตรวจวันสิ้นสุดการอนุญาตให้อยู่", detail: "Re-entry ใช้ได้ไม่เกินวันสิ้นสุดการอนุญาตให้อยู่ จึงต้องดูวันดังกล่าวก่อนเลือกแบบ" },
      { title: "เลือก Single หรือ Multiple", detail: "ถ้าเดินทางออกมากกว่าหนึ่งครั้งภายในช่วงเวลาที่เหลือ แบบหลายครั้งมักคุ้มกว่าในแง่จำนวนรอบการยื่น" },
      { title: "ยื่นก่อนวันเดินทาง", detail: "ยื่นที่สำนักงานตรวจคนเข้าเมือง หรือเคาน์เตอร์สนามบินตามที่เปิดให้บริการ โดยเผื่อเวลาก่อนเช็กอิน" },
      { title: "ตรวจตราประทับก่อนออกเดินทาง", detail: "ตรวจว่าตราประทับ Re-entry ระบุประเภทและวันหมดอายุถูกต้องตรงกับแผนการเดินทาง" },
    ],
    tipsTh: [
      "ถ้าเปลี่ยนเล่มหนังสือเดินทาง ให้ตรวจกับสำนักงานว่าต้องย้ายตราประทับหรือไม่",
      "ยื่นที่สำนักงานล่วงหน้าจะลดความเสี่ยงกว่ายื่นที่สนามบินในวันเดินทาง",
    ],
    warningsTh: [
      "เดินทางออกนอกประเทศโดยไม่มี Re-entry Permit ทำให้การอนุญาตให้อยู่ต่อที่เหลือสิ้นผล",
      "Re-entry Permit ไม่ใช่วีซ่า และไม่ขยายวันสิ้นสุดการอนุญาตให้อยู่",
    ],
    citationTh: "อ้างอิง: พ.ร.บ. คนเข้าเมือง พ.ศ. 2522 มาตรา 39 · สำนักงานตรวจคนเข้าเมือง (immigration.go.th)",
  },
  {
    slug: "residence-certificate",
    th: "หนังสือรับรองถิ่นที่อยู่ (Residence Certificate)",
    en: "Certificate of residence",
    authorityTh: "สำนักงานตรวจคนเข้าเมือง หรือสถานทูต/สถานกงสุลของผู้ยื่น",
    channelsTh: ["สำนักงานตรวจคนเข้าเมืองพื้นที่ตามที่เปิดให้บริการ", "สถานทูตหรือสถานกงสุลของประเทศผู้ยื่น"],
    summaryTh:
      "หนังสือรับรองถิ่นที่อยู่ใช้ยืนยันที่พักอาศัยในไทย มักถูกขอเมื่อทำใบขับขี่ ซื้อยานพาหนะ เปิดบัญชีบางประเภท หรือทำนิติกรรมบางอย่าง ออกได้จากสำนักงานตรวจคนเข้าเมืองหรือสถานทูตของผู้ยื่น แล้วแต่หน่วยงานปลายทางจะรับแบบใด จึงควรถามหน่วยงานปลายทางก่อนว่าต้องการฉบับจากที่ใดและอายุกี่เดือน",
    factsTh: [
      { label: "ผู้ออกเอกสาร", value: "สำนักงานตรวจคนเข้าเมือง หรือสถานทูต/สถานกงสุลของผู้ยื่น" },
      { label: "ใช้ทำอะไรบ่อย", value: "ทำใบขับขี่ ซื้อรถ/จักรยานยนต์ ธุรกรรมที่ต้องยืนยันที่อยู่" },
      { label: "อายุการใช้งาน", value: "หน่วยงานปลายทางมักกำหนดอายุเอง — ยืนยันก่อนยื่น" },
      { label: "เอกสารพื้นฐาน", value: "หนังสือเดินทาง หลักฐานที่พัก และหลักฐานการแจ้งที่พัก (ตม.30)" },
    ],
    requirementsTh: [
      "หนังสือเดินทางพร้อมสำเนาหน้าข้อมูล วีซ่า และตราประทับล่าสุด",
      "หลักฐานการแจ้งที่พัก (ตม.30) และใบรับรายงานตัว 90 วัน (ถ้ามี)",
      "สัญญาเช่าหรือทะเบียนบ้านที่ระบุที่พักปัจจุบัน",
      "รูปถ่ายตามขนาดที่หน่วยงานกำหนด",
      "แผนที่ที่พักหรือหลักฐานยืนยันที่ตั้ง ตามที่สำนักงานร้องขอ",
    ],
    stepsTh: [
      { title: "ถามหน่วยงานปลายทางก่อน", detail: "ยืนยันว่าต้องการหนังสือรับรองจากตรวจคนเข้าเมืองหรือจากสถานทูต และต้องออกภายในกี่เดือน" },
      { title: "ทำข้อมูลที่พักให้เป็นปัจจุบัน", detail: "ตรวจว่าการแจ้งที่พัก (ตม.30) และที่อยู่ที่ใช้รายงานตัว 90 วัน ตรงกับที่พักจริง" },
      { title: "ยื่นคำขอพร้อมเอกสาร", detail: "ยื่นที่หน่วยงานที่เลือก และรับใบนัดตามรอบที่สำนักงานกำหนด" },
      { title: "ตรวจข้อมูลบนเอกสารก่อนนำไปใช้", detail: "ตรวจการสะกดชื่อ เลขหนังสือเดินทาง และที่อยู่ให้ตรงกับเอกสารที่จะใช้ยื่นต่อ" },
    ],
    tipsTh: [
      "ถ้าจะใช้ทำใบขับขี่ ให้ถามกรมการขนส่งทางบกสาขาที่จะยื่นถึงอายุเอกสารที่รับก่อน",
      "ขอสำเนาเพิ่มไว้หลายชุด เพราะหลายหน่วยงานเก็บต้นฉบับ",
    ],
    warningsTh: [
      "เอกสารที่ออกนานเกินกว่าที่ปลายทางรับ จะถูกตีกลับแม้ข้อมูลถูกต้อง",
      "ที่อยู่บนหนังสือรับรองต้องตรงกับที่อยู่ที่แจ้งไว้กับตรวจคนเข้าเมือง",
    ],
    citationTh: "อ้างอิง: สำนักงานตรวจคนเข้าเมือง (immigration.go.th) — เงื่อนไขและช่องทางต่างกันตามสำนักงานพื้นที่",
  },
  {
    slug: "abtc",
    th: "บัตรเดินทางนักธุรกิจเอเปค (APEC Business Travel Card — ABTC)",
    en: "APEC Business Travel Card",
    authorityTh: "กรมการกงสุล กระทรวงการต่างประเทศ (สำหรับผู้ถือหนังสือเดินทางไทย)",
    channelsTh: ["กรมการกงสุล กระทรวงการต่างประเทศ", "ระบบรับคำร้องออนไลน์ตามที่กรมการกงสุลเปิดให้บริการ"],
    summaryTh:
      "ABTC เป็นบัตรสำหรับนักธุรกิจในเขตเศรษฐกิจเอเปค ใช้อำนวยความสะดวกในการเดินทางเชิงธุรกิจไปยังเขตเศรษฐกิจสมาชิกที่ให้การอนุมัติล่วงหน้า และใช้ช่องทางพิเศษที่สนามบินตามที่แต่ละเขตเศรษฐกิจกำหนด การอนุมัติเป็นรายเขตเศรษฐกิจ จึงอาจได้รับไม่ครบทุกแห่งพร้อมกัน",
    factsTh: [
      { label: "หน่วยงานผู้รับคำร้อง (ไทย)", value: "กรมการกงสุล กระทรวงการต่างประเทศ (consular.mfa.go.th)" },
      { label: "ลักษณะการอนุมัติ", value: "แต่ละเขตเศรษฐกิจสมาชิกพิจารณาอนุมัติล่วงหน้าแยกกัน" },
      { label: "ใช้กับการเดินทางแบบใด", value: "การเดินทางเพื่อธุรกิจระยะสั้น ไม่ใช่การทำงานประจำในเขตเศรษฐกิจปลายทาง" },
      { label: "เอกสารเด่น", value: "หลักฐานสถานะนักธุรกิจ/ผู้บริหาร และหนังสือรับรองนิติบุคคล" },
    ],
    requirementsTh: [
      "หนังสือเดินทางที่มีอายุเหลือเพียงพอตามที่กำหนด",
      "หนังสือรับรองนิติบุคคลและหลักฐานตำแหน่งในบริษัท",
      "หลักฐานการค้า/การลงทุนระหว่างประเทศตามที่หน่วยงานกำหนด",
      "แบบคำร้องตามที่กรมการกงสุลกำหนด พร้อมรูปถ่าย",
      "หลักฐานไม่มีประวัติต้องห้ามตามเงื่อนไขของโครงการ",
    ],
    stepsTh: [
      { title: "ตรวจคุณสมบัติผู้ยื่น", detail: "ตรวจว่าผู้ยื่นเป็นนักธุรกิจหรือผู้บริหารที่มีการเดินทางเชิงธุรกิจในเขตเศรษฐกิจเอเปคตามเงื่อนไขของโครงการ" },
      { title: "จัดเตรียมเอกสารบริษัท", detail: "รวบรวมหนังสือรับรองนิติบุคคล หลักฐานตำแหน่ง และหลักฐานการค้าระหว่างประเทศ" },
      { title: "ยื่นคำร้องต่อกรมการกงสุล", detail: "ยื่นตามช่องทางที่กรมการกงสุลเปิดให้บริการ และติดตามสถานะการอนุมัติรายเขตเศรษฐกิจ" },
      { title: "รับบัตรและตรวจรายชื่อเขตเศรษฐกิจ", detail: "ตรวจด้านหลังบัตรว่ามีรหัสเขตเศรษฐกิจใดบ้างที่อนุมัติแล้ว ก่อนวางแผนการเดินทาง" },
    ],
    tipsTh: [
      "วางแผนล่วงหน้าเพราะการอนุมัติจากหลายเขตเศรษฐกิจใช้เวลาไม่เท่ากัน",
      "ตรวจด้านหลังบัตรทุกครั้งก่อนเดินทาง ว่าเขตเศรษฐกิจปลายทางอนุมัติแล้วหรือยัง",
    ],
    warningsTh: [
      "ABTC ไม่ใช่ใบอนุญาตทำงานในเขตเศรษฐกิจปลายทาง — ใช้เพื่อการเดินทางธุรกิจระยะสั้นเท่านั้น",
      "เขตเศรษฐกิจบางแห่งเข้าร่วมเฉพาะช่องทางพิเศษ ไม่ได้ให้การอนุมัติล่วงหน้าเสมอไป",
    ],
    citationTh: "อ้างอิง: กรมการกงสุล กระทรวงการต่างประเทศ (consular.mfa.go.th) — เงื่อนไขโครงการเป็นไปตามที่เอเปคและแต่ละเขตเศรษฐกิจกำหนด",
  },
];

export const WP_TOPIC_BY_SLUG: Record<string, WpTopic> = Object.fromEntries(
  WP_TOPICS.map((t) => [t.slug, t]),
);
